import { Block } from "app/shared/components/blocks/block";
import { ACCOUNT } from "app/shared/services/services-model.type";
import { spacing } from "app/shared/styles/spacing";
import { getAccountStatus } from "app/shared/util/common";
import { formatNumberThousand } from "app/shared/util/format-currency";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";
import { useTheme, DataTable, Paragraph } from "react-native-paper";

interface AccountRollItemProps {
  data: { Payments: ACCOUNT[]; Terms: ACCOUNT[]; Loans: ACCOUNT[] };
  onPressItem: Function;
}

const AccountRollTable = (props: AccountRollItemProps) => {
  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        tableHeader: { backgroundColor: colors.primary, borderBottomWidth: 0 },
        tableRow: { borderBottomWidth: 0 },
        titleContentStyle: {
          ...fonts.semibold,
          color: colors.text,
          fontSize: 13,
        },
        smallCellStyles: {
          minWidth: 120,
          justifyContent: "center",
        },
        mediumCellStyles: {
          minWidth: 150,
          justifyContent: "center",
        },
        largeCellStyles: {
          flex: 1,
          minWidth: 250,
          justifyContent: "center",
        },
        textTitle: { color: colors.accent, fontWeight: "700" },
        textTab: { color: colors.text, fontWeight: "700" },
        cellContent: {
          ...fonts.medium,
          fontSize: 13,
        },
        cellStyles: {
          flex: 1,
          alignSelf: "center",
          textAlignVertical: "center",
          paddingHorizontal: 4,
        },
      }),
    [colors.accent, colors.primary, colors.text, fonts.medium, fonts.semibold],
  );

  const { data, onPressItem } = props;

  const ItemTitle = ({ title = "" }) => (
    <DataTable.Row style={styles.tableRow}>
      <DataTable.Title>
        <Paragraph style={styles.textTitle}>{title}</Paragraph>
      </DataTable.Title>
    </DataTable.Row>
  );

  const ItemCell = ({ title = "" }) => (
    <View style={styles.cellStyles}>
      <Paragraph numberOfLines={10} style={styles.cellContent}>
        {title}
      </Paragraph>
    </View>
  );

  const ItemRow = (account: ACCOUNT, index: number) => {
    return (
      <DataTable.Row
        onPress={() => onPressItem(account)}
        key={`${account.ACCOUNTNO}` + index}
        style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? colors.surface : colors.backgroundForm }]}
        borderless={true}>
        <ItemCell title={account.ACCOUNTNO} />
        <ItemCell title={account.ACCOUNTTYPENAME} />
        <ItemCell title={formatNumberThousand(account.CURRENTBALANCE)} />
        <ItemCell title={account.CURRENCYCODE} />
        <ItemCell title={getAccountStatus(account.STATUS)} />
      </DataTable.Row>
    );
  };

  const TableHeader = (
    <DataTable.Header style={styles.tableHeader}>
      <View style={styles.cellStyles}>
        <Paragraph numberOfLines={10} style={[styles.titleContentStyle]}>
          {t("ACC_SO_TAI_KHOAN")}
        </Paragraph>
      </View>

      <View style={styles.cellStyles}>
        <Paragraph numberOfLines={10} style={[styles.titleContentStyle]}>
          {t("ACC_LOAI_TAI_KHOAN")}
        </Paragraph>
      </View>

      <View style={styles.cellStyles}>
        <Paragraph numberOfLines={10} style={[styles.titleContentStyle]}>
          {t("ACC_SO_DU_HIEN_TAI")}
        </Paragraph>
      </View>

      <View style={styles.cellStyles}>
        <Paragraph numberOfLines={10} style={[styles.titleContentStyle]}>
          {t("ACC_DON_VI")}
        </Paragraph>
      </View>

      <View style={styles.cellStyles}>
        <Paragraph numberOfLines={10} style={[styles.titleContentStyle]}>
          {t("ACC_TRANG_THAI")}
        </Paragraph>
      </View>
    </DataTable.Header>
  );

  return (
    <Block pb={spacing.huge} ph={isDesktop ? spacing.galactic : spacing.huge}>
      <DataTable>
        {TableHeader}
        {data.Payments.length > 0 ? (
          <React.Fragment>
            <ItemTitle title={t("ACC_TAI_KHOAN_THANH_TOAN")} />
            <React.Fragment>{data.Payments.map(ItemRow)}</React.Fragment>
          </React.Fragment>
        ) : null}
        {data.Terms.length > 0 ? (
          <React.Fragment>
            <ItemTitle title={t("ACC_TAI_KHOAN_TIET_KIEM")} />
            <React.Fragment>{data.Terms.map(ItemRow)}</React.Fragment>
          </React.Fragment>
        ) : null}
        {data.Loans.length > 0 ? (
          <React.Fragment>
            <ItemTitle title={t("ACC_MAN_TAI_KHOAN_VAY")} />
            <React.Fragment>{data.Loans.map(ItemRow)}</React.Fragment>
          </React.Fragment>
        ) : null}
      </DataTable>
    </Block>
  );
};

export default AccountRollTable;
